import React, { useMemo, useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircadiaBarChart from './CircadiaBarChart';
import type { GraphDistribution } from '../GraphBox';

interface HalfWidthBarChartProps {
    title: string;
    data: GraphDistribution[];
    xaxislabel: string;
    yaxislabel: string;
    registerExportFn?: (exportFn: () => void) => void;
    [key: string]: any; // For any additional props,
    fullWidth?: boolean;
}

/**
 * CircadiaHalfWidthBarChart - A wrapper around CircadiaBarChart that applies half-width sizing
 * All props are passed through to CircadiaBarChart
 */
const CircadiaHalfWidthBarChart: React.FC<HalfWidthBarChartProps> = (props) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
	const isLg = useMediaQuery(theme.breakpoints.between('lg', 'xl'));
	const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));

	// Track viewport size with state
	const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

	// Add window resize listener
	useEffect(() => {
		const handleResize = () => {
			setViewportWidth(window.innerWidth);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const chartSize = useMemo(() => {
		if (isMobile) {
			if (viewportWidth < 500) {
				return { width: viewportWidth - 50, height: 200 };
			}
			return { width: 400, height: 200 };
		}
		if (isMd) return { width: 400, height: 260 };
		if (isLg) return { width: 500, height: 300 };
		if (isXlUp) return { width: 600, height: 300 };
		return { width: 500, height: 300 };
	}, [viewportWidth, isMobile, isMd, isLg, isXlUp]);

	return (
		<CircadiaBarChart
			{...props} // Pass all props through
			chartSize={chartSize} // Override with our custom chart size
		/>
	);
};

export default CircadiaHalfWidthBarChart;
